import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Play, Radio } from "lucide-react";
import { api } from "../api";
import { mediaUrl, thumbUrl, fmtNum, fmtDur } from "../config";
import { VideoCard, Avatar, SectionTitle, Spinner, Empty } from "../components/cards";

// Search everything in the app: people, videos, shorts, series, episodes, live and past live.
// Uses the same /search endpoint plus the public content lists, then groups by type.
const Search = () => {
  const [params] = useSearchParams();
  const q = (params.get("q") || "").trim();
  const [server, setServer] = useState(null);
  const [shorts, setShorts] = useState([]);
  const [series, setSeries] = useState([]);
  const [episodesBySeries, setEpisodesBySeries] = useState({});
  const [live, setLive] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // Server search (videos + creators + music)
  useEffect(() => {
    if (!q) {
      setServer({ videos: [], creators: [], music: [] });
      return;
    }
    setServer(null);
    api.get(`/search?q=${encodeURIComponent(q)}`)
      .then((r) => setServer(Array.isArray(r) ? { videos: r, creators: [], music: [] } : { videos: r.videos || [], creators: r.creators || r.users || [], music: r.music || r.tracks || [] }))
      .catch(() => setServer({ videos: [], creators: [], music: [] }));
  }, [q]);

  // Fetch supporting content (shorts, series, live) for grouping.
  // Series episode details are only fetched when there's a query (to avoid N requests up-front).
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const [sh, se, li] = await Promise.all([
        api.get("/videos?format=short&limit=60").then((r) => (Array.isArray(r) ? r : r.videos || [])).catch(() => []),
        api.get("/series?limit=60").then((r) => (Array.isArray(r) ? r : r.series || [])).catch(() => []),
        api.get("/live/streams").then((r) => (Array.isArray(r) ? r : r.streams || [])).catch(() => []),
      ]);
      if (cancelled) return;
      setShorts(sh);
      setSeries(se);
      setLive(li);
    };
    run();
    return () => { cancelled = true; };
  }, []);

  // Fetch episodes per series only when a query is present (for episode search).
  useEffect(() => {
    if (!q || series.length === 0) {
      setLoaded(true);
      return;
    }
    let cancelled = false;
    setLoaded(false);
    Promise.all(
      series.map((s) =>
        api.get(`/series/${s.id}`).then((r) => [s.id, r.episodes ?? r.series?.episodes ?? []]).catch(() => [s.id, []])
      )
    ).then((entries) => {
      if (cancelled) return;
      setEpisodesBySeries(Object.fromEntries(entries));
      setLoaded(true);
    });
    return () => { cancelled = true; };
  }, [q, series]);

  const ql = q.toLowerCase();
  const matches = (item, fields) => ql && fields.some((f) => (f || "").toLowerCase().includes(ql));

  const results = useMemo(() => {
    if (!q) return null;
    const norm = (v) => ({ ...v, format: v.format || (v.isShort ? "short" : "video") });

    const allVideos = (server?.videos || []).map(norm);
    const videos = allVideos.filter((v) => (v.format || "video") !== "short");
    const videoShorts = allVideos.filter((v) => v.format === "short");

    // local shorts (dedupe against server-returned shorts)
    const serverShortIds = new Set(videoShorts.map((v) => v.id));
    const localShorts = shorts
      .filter((s) => !serverShortIds.has(s.id))
      .filter((s) => matches(s, [s.title, s.creator?.displayName, s.creator?.username, s.description]));

    const seriesMatches = series.filter((s) => matches(s, [s.title, s.creator?.displayName, s.creator?.username, s.description, s.genre]));

    const episodeMatches = [];
    Object.entries(episodesBySeries).forEach(([sid, eps]) => {
      const parent = series.find((x) => String(x.id) === String(sid));
      (eps || []).forEach((ep) => {
        if (matches(ep, [ep.title, ep.description, parent?.title])) {
          episodeMatches.push({ ...ep, seriesId: sid, seriesTitle: parent?.title });
        }
      });
    });

    const liveMatches = live.filter((s) => (s.isLive || s.status === "live") && matches(s, [s.title, s.creator?.displayName, s.creator?.username]));
    const pastLiveMatches = live.filter((s) => !(s.isLive || s.status === "live") && matches(s, [s.title, s.creator?.displayName, s.creator?.username]));

    return {
      people: server?.creators || [],
      videos,
      shorts: [...videoShorts, ...localShorts],
      series: seriesMatches,
      episodes: episodeMatches,
      live: liveMatches,
      pastLive: pastLiveMatches,
      music: server?.music || [],
    };
  }, [q, server, shorts, series, episodesBySeries, live]);

  if (!q) {
    return (
      <div className="mx-auto max-w-7xl" data-testid="search-page">
        <Empty>Type in the search box above to find people, videos, shorts, series, episodes and live streams.</Empty>
      </div>
    );
  }

  if (server === null || results === null || !loaded) return <Spinner />;

  const total =
    results.people.length + results.videos.length + results.shorts.length + results.series.length +
    results.episodes.length + results.live.length + results.pastLive.length + results.music.length;

  const ytThumb = (s) => (s.youtubeId ? `https://i.ytimg.com/vi/${s.youtubeId}/hqdefault.jpg` : "");

  return (
    <div className="mx-auto max-w-7xl" data-testid="search-page">
      <h1 className="mb-6 font-heading text-2xl font-bold tracking-tight text-white">
        Results for <span className="text-crimson">“{q}”</span>
      </h1>
      {total === 0 && <Empty>Nothing found. Try another search.</Empty>}

      {results.people.length > 0 && (
        <section className="mb-8" data-testid="search-people">
          <SectionTitle>People</SectionTitle>
          <div className="flex flex-wrap gap-3">
            {results.people.map((c) => (
              <Link key={c.id} to={`/app/creator/${c.id}`} className="flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.03] py-2 pl-2 pr-5 transition-colors hover:border-crimson/40" data-testid={`search-creator-${c.id}`}>
                <Avatar user={c} size={34} />
                <span className="font-heading text-sm font-semibold text-white">{c.displayName || c.username}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {results.videos.length > 0 && (
        <section className="mb-8" data-testid="search-videos">
          <SectionTitle>Videos</SectionTitle>
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {results.videos.map((v) => <VideoCard key={v.id} video={v} />)}
          </div>
        </section>
      )}

      {results.shorts.length > 0 && (
        <section className="mb-8" data-testid="search-shorts">
          <SectionTitle>Shorts</SectionTitle>
          <div className="flex flex-wrap gap-4">
            {results.shorts.map((s) => (
              <Link key={s.id} to="/app/shorts" className="group block w-32 shrink-0 overflow-hidden rounded-2xl border border-white/10 transition-colors hover:border-crimson/40 sm:w-36" data-testid={`search-short-${s.id}`}>
                <div className="aspect-[9/16] bg-surface">
                  {thumbUrl(s) ? (
                    <img src={thumbUrl(s)} alt={s.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center"><Play size={24} className="text-slate-600" /></div>
                  )}
                </div>
                <div className="p-2.5">
                  <p className="truncate text-xs font-semibold text-white">{s.title}</p>
                  <p className="truncate font-mono text-[9px] uppercase tracking-wider text-slate-500">{s.creator?.displayName || s.creator?.username}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {results.series.length > 0 && (
        <section className="mb-8" data-testid="search-series">
          <SectionTitle>Series</SectionTitle>
          <div className="flex flex-wrap gap-4">
            {results.series.map((s) => {
              const img = mediaUrl(s.cover || s.coverImage || s.thumbnail);
              return (
                <Link key={s.id} to={`/app/series/${s.id}`} className="group block w-40 shrink-0 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] transition-colors hover:border-crimson/40 sm:w-44" data-testid={`search-series-${s.id}`}>
                  <div className="relative aspect-[3/4] overflow-hidden bg-surface">
                    {img ? (
                      <img src={img} alt={s.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center font-heading text-3xl font-black text-slate-700">{s.title?.[0]}</div>
                    )}
                    {s.episodeCount > 0 && (
                      <span className="absolute bottom-2 right-2 rounded-md bg-black/80 px-1.5 py-0.5 font-mono text-[10px] text-white">{s.episodeCount} ep{s.episodeCount === 1 ? "" : "s"}</span>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="truncate font-heading text-sm font-semibold text-white">{s.title}</p>
                    <p className="truncate text-xs text-slate-500">{s.creator?.displayName || s.genre || ""}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {results.episodes.length > 0 && (
        <section className="mb-8" data-testid="search-episodes">
          <SectionTitle>Episodes</SectionTitle>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {results.episodes.map((ep) => (
              <Link key={ep.id} to={`/app/series/${ep.seriesId}`} className="flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.02] p-3 transition-colors hover:border-crimson/40" data-testid={`search-episode-${ep.id}`}>
                <div className="relative h-16 w-28 shrink-0 overflow-hidden rounded-lg bg-surface">
                  {mediaUrl(ep.thumbnail) && <img src={mediaUrl(ep.thumbnail)} alt="" className="h-full w-full object-cover" />}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30"><Play size={16} className="text-white" fill="white" /></div>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-heading text-sm font-semibold text-white">
                    {ep.seasonNumber != null ? `S${ep.seasonNumber} · E${ep.episodeNumber} — ` : ""}{ep.title}
                  </p>
                  {ep.seriesTitle && <p className="truncate text-xs text-slate-500">{ep.seriesTitle}</p>}
                  {ep.durationSec > 0 && <p className="font-mono text-[10px] text-slate-500">{fmtDur(ep.durationSec)}</p>}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {results.live.length > 0 && (
        <section className="mb-8" data-testid="search-live">
          <SectionTitle>Live</SectionTitle>
          <div className="flex flex-wrap gap-4">
            {results.live.map((s) => (
              <Link key={s.id} to={`/app/live/${s.id}`} className="group block w-64 shrink-0 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] transition-colors hover:border-crimson/40" data-testid={`search-live-${s.id}`}>
                <div className="relative aspect-video overflow-hidden bg-surface">
                  {ytThumb(s) && <img src={ytThumb(s)} alt={s.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                  <span className="absolute left-2 top-2 flex items-center gap-1.5 rounded-full bg-crimson px-2.5 py-1 font-mono text-[10px] font-medium uppercase tracking-widest text-white">
                    <Radio size={10} /> Live
                  </span>
                </div>
                <div className="p-3">
                  <p className="truncate font-heading text-sm font-semibold text-white">{s.title}</p>
                  <p className="truncate text-xs text-slate-500">{s.creator?.displayName || s.creator?.username}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {results.pastLive.length > 0 && (
        <section className="mb-8" data-testid="search-past-live">
          <SectionTitle>Past Live</SectionTitle>
          <div className="flex flex-wrap gap-4">
            {results.pastLive.map((s) => (
              <Link key={s.id} to={`/app/live/${s.id}`} className="group block w-64 shrink-0 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] transition-colors hover:border-crimson/40" data-testid={`search-past-live-${s.id}`}>
                <div className="relative aspect-video overflow-hidden bg-surface">
                  {ytThumb(s) && <img src={ytThumb(s)} alt={s.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                  <span className="absolute bottom-1.5 right-1.5 rounded-md bg-black/80 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-white">Past live</span>
                </div>
                <div className="p-3">
                  <p className="truncate font-heading text-sm font-semibold text-white">{s.title}</p>
                  <p className="truncate text-xs text-slate-500">{s.creator?.displayName || s.creator?.username}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {results.music.length > 0 && (
        <section data-testid="search-music">
          <SectionTitle>Music</SectionTitle>
          <div className="space-y-1">
            {results.music.map((t) => (
              <Link key={t.id} to="/app/music" className="flex items-center gap-4 rounded-xl px-3 py-2.5 transition-colors hover:bg-white/[0.04]" data-testid={`search-track-${t.id}`}>
                <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg bg-surface">
                  {mediaUrl(t.artwork) && <img src={mediaUrl(t.artwork)} alt="" className="h-full w-full object-cover" />}
                </div>
                <div>
                  <p className="font-heading text-sm font-semibold text-white">{t.title}</p>
                  <p className="text-xs text-slate-500">{t.artist}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default Search;
